import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "react-loader-spinner";
import {
  MdLockOutline,
  MdVisibility,
  MdVisibilityOff,
  MdWaterDrop,
  MdCheckCircle,
  MdErrorOutline,
} from "react-icons/md";
import { GoArrowRight } from "react-icons/go";
import { WR } from "../components/ui/tokens";
import { setShopPassword, InvalidTokenError } from "../auth/shopPasswordApi";

// /new-shop-password?token=… — a shop account was created in the admin panel
// and the invite email links here so the manager can choose their first
// password. The token is a Devise reset token; see shopPasswordApi for why this
// cannot reuse the customer web app's /new-user-password endpoints.
//
// Deliberately NOT wrapped in PublicRoutes: that guard bounces an authenticated
// visitor to /start, and this link is routinely opened on a till that is already
// signed in as another shop. For the same reason the page never touches the
// stored session — setting a password elsewhere must not sign the till out.
//
// There is no endpoint to validate a shop token before use, so the form is
// shown straight away and a rejected token only surfaces on submit.
// Devise config.password_length = 6..128. Checked here rather than with the
// input's minLength attribute: the browser's native "too short" bubble fires
// only on typed input and in the browser's own language, so it would pre-empt
// the translated message on some submissions and not others.
const MIN_PASSWORD_LENGTH = 6;

const NewShopPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token"),
    []
  );

  const [status, setStatus] = useState(token ? "form" : "invalid");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t("NSP_ERROR_TOO_SHORT", { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirmation) {
      setError(t("NSP_ERROR_MISMATCH"));
      return;
    }

    setSubmitting(true);
    try {
      await setShopPassword({
        token,
        password,
        passwordConfirmation: confirmation,
      });
      setStatus("done");
    } catch (err) {
      if (err instanceof InvalidTokenError) {
        setStatus("invalid");
      } else {
        setError(
          err.message === "NETWORK" ? t("NSP_ERROR_NETWORK") : err.message
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldWrap = (name) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    height: 54,
    padding: "0 14px",
    borderRadius: WR.radius.md,
    background: WR.color.surface,
    border: `1.5px solid ${
      focused === name ? WR.color.brand : WR.color.border
    }`,
    boxShadow:
      focused === name
        ? "0 0 0 4px rgba(61,69,134,0.15), 0 10px 26px -12px rgba(61,69,134,0.45)"
        : "none",
    transition: "border-color .15s ease, box-shadow .15s ease",
  });
  const inputStyle = {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: "none",
    background: "transparent",
    fontSize: WR.text.slot,
    fontWeight: WR.weight.semibold,
    color: WR.color.encre,
    fontFamily: WR.font.sans,
  };
  const fieldLabel = {
    display: "block",
    fontSize: WR.text.small,
    fontWeight: WR.weight.bold,
    color: WR.color.inkMuted,
    margin: "0 0 7px",
  };
  const cta = {
    width: "100%",
    height: 56,
    borderRadius: WR.radius.pill,
    background: WR.color.brand,
    color: WR.color.textOnBrand,
    border: 0,
    fontSize: WR.text.bodyLg,
    fontWeight: WR.weight.bold,
    boxShadow: WR.shadow.cta,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    fontFamily: WR.font.sans,
  };

  const badge = (tone) => ({
    width: 64,
    height: 64,
    borderRadius: WR.radius.pill,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
    background: tone === "success" ? "rgba(26,156,104,0.12)" : "#FFF8E1",
    color: tone === "success" ? WR.color.success : WR.color.warning,
  });

  const title = {
    fontSize: "clamp(26px,3.2vw,32px)",
    fontWeight: WR.weight.extra,
    letterSpacing: WR.ls.snug,
    color: WR.color.encre,
    margin: "0 0 8px",
  };
  const lede = {
    fontSize: WR.text.slot,
    lineHeight: WR.lh.body,
    color: WR.color.inkMuted,
    margin: "0 0 26px",
  };

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: WR.color.bg,
        fontFamily: WR.font.sans,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 20px",
      }}
    >
      <div style={{ width: "100%", maxWidth: 440 }}>
        {/* brand mark — same lockup as the login hero */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            marginBottom: 24,
            color: WR.color.brand,
          }}
        >
          <span
            style={{
              width: 42,
              height: 42,
              borderRadius: WR.radius.sm,
              background: WR.color.lavSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdWaterDrop size={22} />
          </span>
          <span
            style={{
              fontSize: 20,
              fontWeight: WR.weight.extra,
              letterSpacing: WR.ls.logo,
            }}
          >
            WOSH
          </span>
        </div>

        <div
          style={{
            background: WR.color.surface,
            borderRadius: WR.radius.card,
            border: `1px solid ${WR.color.border}`,
            boxShadow: WR.shadow.card,
            padding: "clamp(24px,4vw,40px)",
            textAlign: status === "form" ? "left" : "center",
          }}
        >
          {status === "invalid" && (
            <>
              <div style={badge("warning")} aria-hidden="true">
                <MdErrorOutline size={32} />
              </div>
              <h1 style={title}>{t("NSP_INVALID_TITLE")}</h1>
              <p style={lede}>{t("NSP_INVALID_BODY")}</p>
              <button
                type="button"
                style={cta}
                onClick={() => navigate("/user/login")}
              >
                {t("NSP_BACK_TO_LOGIN")}
              </button>
            </>
          )}

          {status === "done" && (
            <>
              <div style={badge("success")} aria-hidden="true">
                <MdCheckCircle size={32} />
              </div>
              <h1 style={title}>{t("NSP_DONE_TITLE")}</h1>
              <p style={lede}>{t("NSP_DONE_BODY")}</p>
              <button
                type="button"
                style={cta}
                onClick={() => navigate("/user/login")}
              >
                {t("SIGN_IN")}
                <GoArrowRight size={18} />
              </button>
            </>
          )}

          {status === "form" && (
            <>
              <div
                style={{
                  fontSize: WR.text.caption,
                  fontWeight: WR.weight.extra,
                  letterSpacing: WR.ls.eyebrow,
                  textTransform: "uppercase",
                  color: WR.color.mintDeep,
                  marginBottom: 8,
                }}
              >
                {t("NSP_EYEBROW")}
              </div>
              <h1 style={title}>{t("NSP_TITLE")}</h1>
              <p style={lede}>{t("NSP_SUBTITLE")}</p>

              {error && (
                <div
                  role="alert"
                  style={{
                    padding: "12px 16px",
                    borderRadius: WR.radius.sm,
                    background: WR.color.errorBg,
                    border: `1px solid ${WR.color.errorBorder}`,
                    color: WR.color.danger,
                    fontSize: WR.text.body,
                    fontWeight: WR.weight.semibold,
                    marginBottom: 18,
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={submit}>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="new-password" style={fieldLabel}>
                    {t("NSP_PASSWORD_LABEL")}
                  </label>
                  <div style={fieldWrap("password")}>
                    <MdLockOutline
                      size={19}
                      color={WR.color.inkSoft}
                      style={{ flexShrink: 0 }}
                    />
                    <input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setFocused("password")}
                      onBlur={() => setFocused(null)}
                      autoComplete="new-password"
                      required
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={
                        showPassword
                          ? t("NSP_HIDE_PASSWORD")
                          : t("NSP_SHOW_PASSWORD")
                      }
                      style={{
                        border: 0,
                        background: "none",
                        padding: 0,
                        cursor: "pointer",
                        color: WR.color.inkSoft,
                        display: "inline-flex",
                        flexShrink: 0,
                      }}
                    >
                      {showPassword ? (
                        <MdVisibilityOff size={20} />
                      ) : (
                        <MdVisibility size={20} />
                      )}
                    </button>
                  </div>
                  <p
                    style={{
                      fontSize: WR.text.caption,
                      color: WR.color.inkSoft,
                      fontWeight: WR.weight.semibold,
                      margin: "7px 0 0",
                    }}
                  >
                    {t("NSP_PASSWORD_HINT", { min: MIN_PASSWORD_LENGTH })}
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label htmlFor="confirm-password" style={fieldLabel}>
                    {t("NSP_CONFIRM_LABEL")}
                  </label>
                  <div style={fieldWrap("confirmation")}>
                    <MdLockOutline
                      size={19}
                      color={WR.color.inkSoft}
                      style={{ flexShrink: 0 }}
                    />
                    <input
                      id="confirm-password"
                      type={showPassword ? "text" : "password"}
                      value={confirmation}
                      onChange={(e) => setConfirmation(e.target.value)}
                      onFocus={() => setFocused("confirmation")}
                      onBlur={() => setFocused(null)}
                      autoComplete="new-password"
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    ...cta,
                    opacity: submitting ? 0.7 : 1,
                    boxShadow: submitting ? "none" : WR.shadow.cta,
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                >
                  {t("NSP_CTA")}
                  {submitting ? (
                    <Loader type="Oval" color="#ffffff" height={20} width={20} />
                  ) : (
                    <GoArrowRight size={18} />
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <div
          style={{
            textAlign: "center",
            fontSize: WR.text.eyebrow,
            fontWeight: WR.weight.bold,
            letterSpacing: WR.ls.label,
            textTransform: "uppercase",
            color: WR.color.inkSoft,
            marginTop: 26,
          }}
        >
          WOSH POS v2
        </div>
      </div>
    </div>
  );
};

export default NewShopPassword;
